import { type ReactNode } from "react";

import { Typography } from "@/design-system/components/Typography/Typography";
import { twMerge } from "@/design-system/twMerge";
import Risk from "@/trackers/Risk";
import {
  WTM_SIGN_IN_HINT,
  WTM_TYPICAL_LOADS_COPY,
} from "@/trackers/wtmConstants";
import type { Wtm2RiskLevel } from "@/trackers/types";

export type WtmPublicTrackerPreviewGuidance = {
  whyFlagged: string;
  whatToDo: string;
  whoCanFixIt: string;
};

function DetailCard({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={twMerge(
        "border-borderSubtle bg-surface-hover flex flex-col gap-1.5 rounded-xl border p-3.5",
        className,
      )}
    >
      <Typography variant="heading-xs" as="h2">
        {title}
      </Typography>
      <div className="text-body-sm text-textDefault">{children}</div>
    </section>
  );
}

export function WtmPublicTrackerPreview({
  trackerName,
  riskLevel,
  description,
  guidance,
  compact = false,
}: {
  trackerName: string;
  riskLevel: Wtm2RiskLevel;
  description: string;
  guidance: WtmPublicTrackerPreviewGuidance;
  compact?: boolean;
}) {
  const whoCanFixIt = guidance.whoCanFixIt.trim();
  const columnCount = whoCanFixIt ? 3 : 2;

  return (
    <div className="flex flex-col gap-3">
      <div
        className="flex flex-col items-start gap-2 text-left"
        data-testid="tracker-summary"
      >
        <Risk riskLevel={riskLevel} description={description} infoMode="none" />
        <Typography variant="heading-xs" as={compact ? "h2" : "h1"}>
          {trackerName}
        </Typography>
      </div>

      <div
        className={
          compact
            ? "grid grid-cols-1 gap-3"
            : columnCount === 3
              ? "grid grid-cols-1 gap-3 lg:grid-cols-3"
              : "grid grid-cols-1 gap-3 lg:grid-cols-2"
        }
        data-testid="tracker-guidance-grid"
      >
        <DetailCard title="Why it's flagged">{guidance.whyFlagged}</DetailCard>
        <DetailCard title="Common fixes">{guidance.whatToDo}</DetailCard>
        {whoCanFixIt ? (
          <DetailCard title="Who resolves it">{whoCanFixIt}</DetailCard>
        ) : null}
        <DetailCard
          title="Where this typically loads"
          className={
            compact
              ? undefined
              : columnCount === 3
                ? "lg:col-span-3"
                : "lg:col-span-2"
          }
        >
          {WTM_TYPICAL_LOADS_COPY}
        </DetailCard>
      </div>

      <Typography variant="body-xs" color="subtle">
        {WTM_SIGN_IN_HINT}
      </Typography>
    </div>
  );
}
